import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { faArrowLeft, faThumbsDown, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";

import { HelpChatFeedback } from "@signalist/shared-types";

import { classNames } from "@/utils/classNames";
import { Icon } from "@/components/Icon/Icon";
import { MarkdownContent } from "@/components/MarkdownContent/MarkdownContent";
import { Spinner } from "@/components/Spinner/Spinner";

import { ROUTE_CONFIG } from "@/routes/config";
import { useGetHelpConversationQuery } from "@/pages/Private/redux/helpCenter/helpConversations.api";
import { HelpConversationMessageInfo } from "@/pages/Private/redux/helpCenter/helpConversations.schema";

const ta = "helpCenter.admin.conversations";

/** The end-user's rating badge on an assistant answer. */
const FeedbackBadge: FunctionComponent<{ feedback: HelpChatFeedback }> = ({ feedback }) => {
	const { t } = useTranslation();
	const isUp = feedback === HelpChatFeedback.UP;

	return (
		<span
			className={classNames(
				"flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
				isUp
					? "bg-status-success-light text-status-success"
					: "bg-status-error-light text-status-error"
			)}
		>
			<Icon icon={isUp ? faThumbsUp : faThumbsDown} />
			{isUp ? t(`${ta}.ratedUp`) : t(`${ta}.ratedDown`)}
		</span>
	);
};

/** One transcript turn: the user question (right) or the grounded assistant answer (left). */
const TranscriptTurn: FunctionComponent<{ message: HelpConversationMessageInfo }> = ({
	message,
}) => {
	const { t } = useTranslation();

	if (message.role === "user") {
		return (
			<div className="flex justify-end">
				<div className="max-w-[85%] rounded-lg bg-blue-action px-3 py-2 text-sm text-white">
					{message.content}
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="max-w-[95%] rounded-lg bg-gray-50 px-3 py-2">
				<MarkdownContent className="text-sm text-text" content={message.content} />
			</div>
			{message.sources && message.sources.length > 0 && (
				<div className="flex flex-wrap gap-1.5 pl-1">
					{message.sources.map((source) => (
						<span
							className="rounded-full border border-border-light px-2.5 py-1 text-xs text-text-meta"
							key={source.slug}
						>
							{source.title}
						</span>
					))}
				</div>
			)}
			{message.feedback && (
				<div className="pl-1">
					<FeedbackBadge feedback={message.feedback} />
				</div>
			)}
			{message.pageContext?.label && (
				<span className="pl-1 text-xs text-text-disabled">
					{t(`${ta}.askedFrom`, { page: message.pageContext.label })}
				</span>
			)}
		</div>
	);
};

/** Read-only transcript of one saved help conversation (GLOBAL_ADMIN support review). */
export const AdminConversationDetail: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { conversationId } = useParams<{ conversationId: string }>();

	const { data, isLoading } = useGetHelpConversationQuery(Number(conversationId), {
		skip: !conversationId,
	});

	const backToList = () => navigate(ROUTE_CONFIG.HELP_CENTER_ADMIN);

	if (isLoading) {
		return (
			<div className="flex justify-center py-10">
				<Spinner />
			</div>
		);
	}

	if (!data) {
		return (
			<div className="flex flex-col items-start gap-3 p-4">
				<button
					className="flex cursor-pointer items-center gap-1.5 text-sm text-blue-action"
					onClick={backToList}
				>
					<Icon icon={faArrowLeft} />
					{t(`${ta}.back`)}
				</button>
				<p className="text-sm text-text-meta">{t(`${ta}.notFound`)}</p>
			</div>
		);
	}

	return (
		<div className="mx-auto flex max-w-3xl flex-col gap-4 p-4">
			<div className="flex items-center justify-between">
				<button
					className="flex cursor-pointer items-center gap-1.5 text-sm text-blue-action"
					onClick={backToList}
				>
					<Icon icon={faArrowLeft} />
					{t(`${ta}.back`)}
				</button>
				<div className="flex items-center gap-3 text-xs text-text-meta">
					<span className="uppercase">{data.language ?? "—"}</span>
					<span>{t(`${ta}.turnsCount`, { count: data.messageCount })}</span>
					<span>{new Date(data.createdAt).toLocaleString()}</span>
				</div>
			</div>

			<div className="flex flex-col gap-3">
				{data.messages.map((message) => (
					<TranscriptTurn key={message.id} message={message} />
				))}
			</div>
		</div>
	);
};
